import {LitElement, css, html} from "lit";
import {mix} from "mixwith";
import SelectionMixin from "./Mixins/SelectionMixin";
import {styleMap} from "lit/directives/style-map.js";
import {classMap} from "lit/directives/class-map.js";
import {renderFrame} from "../Utils/LitUtils.js";

export class AuiPages extends mix(LitElement).with(SelectionMixin) {

	static get properties() {
		return {
			usePageHeight: Boolean,
			disableAnimation: Boolean,

			// Private
			__viewMaxHeight: { type: Number, attribute: false },
		};
	}

	set usePageHeight(bool) {
		if(typeof bool !== "boolean")
			return;

		const oldBool = this.__usePageHeight;
		const hasChanged = bool !== oldBool;

		if(hasChanged) {
			if(bool) {
				this._resizeObserer = new ResizeObserver(this.onSizeChanged.bind(this));

				// Observe active page, if previously was not observed
				if(oldBool === false) {
					if(this.pages[this.selected])
						this._resizeObserer.observe(this.pages[this.selected]);
				}
			} else {
				this._resizeObserer.disconnect();
				this._resizeObserer = null;
				this.__viewMaxHeight = undefined;
			}

			this.__usePageHeight = bool;
		}
	}

	get usePageHeight() {
		return this.__usePageHeight;
	}

	get items() {
		return this.pages;
	}

	set selected(value) {
		if(this.items.length === 0) {
			// No items available to be selected, store the value temporarily until items are available.
			this._selected = value;
		} else {
			super.selected = value;
		}
	}

	get selected() {
		return super.selected;
	}

	constructor() {
		super();
		this._resizeObserer = null;

		/**
		 * Currently available pages (elements) in the slot.
		 *
		 * @type {Element[]}
		 */
		this.pages = [];

		/**
		 * The active/selected index.
		 *
		 * @type {number}
		 */
		this.selected = 0;

		/**
		 *  true: Container will always be the height of the selected page.
		 * false: Container will always be the height of the largest page.
		 *
		 * @type {boolean}
		 */
		this.usePageHeight = true;

		/**
		 * Disables the animation.
		 *
		 * @type {boolean}
		 */
		this.disableAnimation = false;
	}

	/**
	 * A wrapper to temporarily disable animation for e.g. selecting a page.
	 *
	 * @param {function} callback
	 */
	async withoutAnimation(callback) {
		const prev = this.disableAnimation;

		this.disableAnimation = true;

		await renderFrame();
		callback();
		await renderFrame();

		this.disableAnimation = prev;

		return renderFrame();
	}

	update(changedProperties) {
		if(changedProperties.has("selected"))
			this.updateActivePage(changedProperties.get("selected"));

		super.update(changedProperties);
	}

	updateActivePage(prevSelected = undefined) {
		if(prevSelected) {
			this.deactivatePage(this.pages[prevSelected]);
			this.activatePage(this.pages[this.selected]);
		} else {
			// The pages probably changes, update all
			this.pages.forEach((page, index) => {
				if(index === this.selected) {
					this.activatePage(page);
				} else
					this.deactivatePage(page);
			});
		}
	}

	activatePage(page) {
		page.setAttribute("active", "");
		if(this._resizeObserer)
			this._resizeObserer.observe(page);
	}

	deactivatePage(page) {
		if(this._resizeObserer)
			this._resizeObserer.unobserve(page);
		page.removeAttribute("active");
	}

	onSlotMutate(e) {
		this.pages = e.target.assignedElements();
		if(this._selected !== undefined) {
			// selected got set before items were available, set it now.
			this.withoutAnimation(() => this.selected = this._selected).then(() => {
				delete this._selected;
			});
		} else
			this.updateActivePage();
	}

	onSizeChanged(entries) {
		this.__viewMaxHeight = (entries[0].borderBoxSize[0] || entries[0].borderBoxSize).blockSize;
	}

	static get styles() {
		return css`
			:host {
				--aui-pages-animation-duration: 0.4s;
				
				display: block;
			}
			
			.container {
				overflow: hidden;
			}
			
			.view {
				display: flex;
				align-items: flex-start;
			}
			
			.view.animate {
				transition-property: max-height, transform;
				transition-duration: var(--aui-pages-animation-duration);
				
				will-change: max-height, transform;
			}
			
			::slotted(*) {
				box-sizing: border-box;
				display: block;
				flex: 0 0 100%;
			}
		`;
	}

	getViewStyle() {
		const offsetX = -(this.selected*100);
		const maxHeight = this.__viewMaxHeight ? this.__viewMaxHeight + "px" : "";

		return {
			maxHeight: maxHeight,
			transform: "translateX(" + offsetX + "%)"
		};
	}

	getViewClassMap() {
		return {
			animate: !this.disableAnimation
		};
	}

	render() {
		return html`
			<div class="container">
				<div class="view ${classMap(this.getViewClassMap())}" style=${styleMap(this.getViewStyle())}>
					<slot @slotchange=${this.onSlotMutate}></slot>
				</div>
			</div>
		`;
	}

}

customElements.define("aui-pages", AuiPages);

export default AuiPages;